import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { LabData, Report } from "@/services/backend/htz/report/types/report";
import {
  updateHtzReportCache,
  PatchRequest,
  useHtzReportPatchMutation,
  useHtzReportShowQuery,
} from "@/services/backend/htz/report/api/report-api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import { Edit, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { TextInput } from "@/shared/components/form/text-input";
import { useErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { Laboratory } from "@/services/backend/samples/types/laboratory";
import { useSamplesLaboratoryListQuery } from "@/services/backend/samples/api/lab-api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";

export function LaboratoriesCard({ report }: { report: Report }) {
  const { data } = useHtzReportShowQuery({ id: report.id });
  const request: PatchRequest = {
    id: report.id,
    laboratories: report.laboratories,
  };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzReportCache,
    useHtzReportPatchMutation(),
  );

  const { resetNotReadyErrorField } = useErrDocumentNotReady();

  if (!data) return;

  const onAdd = (lab: LabData) => {
    if (data.laboratories.some((l) => l.labId === lab.labId)) {
      return;
    }
    const laboratories = [...data.laboratories, lab];
    resetNotReadyErrorField("laboratories");
    set({ ...request, laboratories }, { ...data, laboratories });
  };

  const onLaboratoryChange = (lab: LabData) => {
    const laboratories = data.laboratories.map((l) =>
      l.labId === lab.labId ? lab : l,
    );
    set({ ...request, laboratories }, { ...data, laboratories });
  };

  const onLaboratoryDelete = (lab: LabData) => {
    const laboratories = data.laboratories.filter((l) => l.labId !== lab.labId);
    set({ ...request, laboratories }, { ...data, laboratories });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="relative">
          <span>{t("Labore")}</span>
          <AddLaboratoryDropdown
            reportId={report.id}
            onAdd={onAdd}
            disabled={report.immutable}
          />
        </CardTitle>
        <CardDescription>
          {t("Daten der Labore, welche Auswertungen gemacht haben.")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Labor")}</TableHead>
              <TableHead>{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.laboratories.map((lab) => (
              <TableRow key={lab.name}>
                <TableCell className="w-full">{lab.name}</TableCell>
                <TableCell className="flex space-x-2 py-1.5 text-right">
                  <PutLaboratoryDialog
                    laboratory={lab}
                    onLaboratoryChange={onLaboratoryChange}
                    disabled={data.immutable}
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => onLaboratoryDelete(lab)}
                    disabled={data.immutable}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function AddLaboratoryDropdown({
  reportId,
  onAdd,
  disabled,
}: {
  reportId: string;
  onAdd: (lab: LabData) => void;
  disabled: boolean;
}) {
  const { data: labList } = useSamplesLaboratoryListQuery({});

  if (!labList) {
    return null;
  }

  const doAdd = (lab: Laboratory) => {
    if (disabled) return;

    onAdd({
      reportId,
      labId: lab.id,
      name: lab.name,
      street: lab.address.street,
      postalCode: lab.address.postalCode,
      city: lab.address.city,
      phone: "",
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button variant="outline" className="absolute top-0 right-0">
          {t("Labor hinzufügen")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuGroup>
          {labList.labs.map((lab) => (
            <DropdownMenuItem key={lab.id} onClick={() => doAdd(lab)}>
              {lab.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PutLaboratoryDialog({
  laboratory,
  onLaboratoryChange,
  disabled,
}: {
  laboratory: LabData;
  onLaboratoryChange: (labs: LabData) => void;
  disabled: boolean;
}) {
  const { notReadyError, resetNotReadyErrorField } = useErrDocumentNotReady();
  const errorPrefix = `laboratories.${laboratory.labId}.`;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Edit className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <TextInput
          label={t("Name")}
          field="name"
          text={laboratory.name}
          onTextChange={(name) => {
            resetNotReadyErrorField(`${errorPrefix}name`);
            onLaboratoryChange({ ...laboratory, name });
          }}
          errors={inputValidationErrorMessages(
            notReadyError,
            `${errorPrefix}name`,
          )}
          disabled={disabled}
        />
        <TextInput
          label={t("Straße")}
          field="street"
          text={laboratory.street}
          onTextChange={(street) => {
            resetNotReadyErrorField(`${errorPrefix}street`);
            onLaboratoryChange({ ...laboratory, street });
          }}
          errors={inputValidationErrorMessages(
            notReadyError,
            `${errorPrefix}street`,
          )}
          disabled={disabled}
        />
        <TextInput
          label={t("PLZ")}
          field="postalCode"
          text={laboratory.postalCode}
          onTextChange={(postalCode) => {
            resetNotReadyErrorField(`${errorPrefix}postalCode`);
            onLaboratoryChange({ ...laboratory, postalCode });
          }}
          errors={inputValidationErrorMessages(
            notReadyError,
            `${errorPrefix}postalCode`,
          )}
          disabled={disabled}
        />
        <TextInput
          label={t("Stadt")}
          field="city"
          text={laboratory.city}
          onTextChange={(city) => {
            resetNotReadyErrorField(`${errorPrefix}city`);
            onLaboratoryChange({ ...laboratory, city });
          }}
          errors={inputValidationErrorMessages(
            notReadyError,
            `${errorPrefix}city`,
          )}
          disabled={disabled}
        />
        <TextInput
          label={t("Telefon")}
          field="phone"
          text={laboratory.phone}
          onTextChange={(phone) => {
            resetNotReadyErrorField(`${errorPrefix}phone`);
            onLaboratoryChange({ ...laboratory, phone });
          }}
          errors={inputValidationErrorMessages(
            notReadyError,
            `${errorPrefix}phone`,
          )}
          disabled={disabled}
        />
      </DialogContent>
    </Dialog>
  );
}
